import React, { useCallback, useEffect, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';
import { Race } from '../types/race';
import { erc20Abi, formatUnits, parseUnits } from 'viem';
import { useEnvAddresses } from '../hooks/useEnvAddresses';

import './BetPreview.css';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { getEnvChain } from '../config/chainConfig';
import { tokenRacerAbi } from '../abi/tokenRacerAbi';
import { calculateRewardMultiplier } from '../utils/calculateRewardMultiplier';
import useAppStore from '../stores/appStore';

const TransactionStatus = {
  WRONG_NETWORK: 'WRONG_NETWORK',
  INSUFFICIENT_ALLOWANCE: 'INSUFFICIENT_ALLOWANCE',
  INSUFFICIENT_BALANCE: 'INSUFFICIENT_BALANCE',
  READY_TO_SUBMIT: 'READY_TO_SUBMIT',
};

const TransactionStatusText = {
  [TransactionStatus.WRONG_NETWORK]: 'Switch Network',
  [TransactionStatus.INSUFFICIENT_ALLOWANCE]: 'Approve',
  [TransactionStatus.INSUFFICIENT_BALANCE]: 'Insufficient balance',
  [TransactionStatus.READY_TO_SUBMIT]: 'Submit',
};

type BetPreviewProps = {
  race: Race;
  bets: { [tokenId: string]: string };
  totalBet: string;
  show: boolean;
  onClose: () => void;
};

const BetPreview = ({ onClose, race, bets, totalBet, show }: BetPreviewProps) => {
  const publicClient = usePublicClient({ chainId: getEnvChain().id });
  const { data: walletClient } = useWalletClient();
  const { chainId } = useAccount();
  const { tokenRacer, usdc } = useEnvAddresses();
  const { isRestricted } = useAppStore();
  const [transactionStatus, setTransactionStatus] = useState(TransactionStatus.INSUFFICIENT_ALLOWANCE);
  const [waitingForTransaction, setWaitingForTransaction] = useState(false);

  const checkTransactionStatus = useCallback(async () => {
    if (!walletClient || !publicClient) return;
    const stakeAmount = parseUnits(totalBet, usdc.decimals);
    const usdcBalance = await publicClient.readContract({
      address: usdc.address,
      abi: erc20Abi,
      functionName: 'balanceOf',
      args: [walletClient.account.address],
    });
    const allowance = await publicClient.readContract({
      address: usdc.address,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [walletClient.account.address, tokenRacer],
    });
    if (usdcBalance < stakeAmount) {
      setTransactionStatus(TransactionStatus.INSUFFICIENT_BALANCE);
      return;
    } else if (chainId !== getEnvChain().id) {
      setTransactionStatus(TransactionStatus.WRONG_NETWORK);
      return;
    } else if (allowance < stakeAmount) {
      setTransactionStatus(TransactionStatus.INSUFFICIENT_ALLOWANCE);
      return;
    } else {
      setTransactionStatus(TransactionStatus.READY_TO_SUBMIT);
    }
  }, [walletClient, publicClient, totalBet, usdc.decimals, usdc.address, tokenRacer, chainId]);

  const checkIsActionable = () => {
    if (!walletClient) return false;
    if (isRestricted) return false;
    switch (transactionStatus) {
      case TransactionStatus.WRONG_NETWORK:
        return true;
      case TransactionStatus.INSUFFICIENT_ALLOWANCE:
        return true;
      case TransactionStatus.INSUFFICIENT_BALANCE:
        return false;
      case TransactionStatus.READY_TO_SUBMIT:
        return true;
      default:
        return false;
    }
  };

  const getTransactionAction = () => {
    switch (transactionStatus) {
      case TransactionStatus.WRONG_NETWORK:
        return switchNetwork;
      case TransactionStatus.INSUFFICIENT_ALLOWANCE:
        return approveAllowance;
      case TransactionStatus.READY_TO_SUBMIT:
        return createRace;
    }
  };

  const getTransactionActionText = () => {
    if (!walletClient) return 'Connect Wallet';
    if (isRestricted) return 'Location Restricted';
    if (waitingForTransaction) return 'Submitting...';
    return TransactionStatusText[transactionStatus];
  };

  const switchNetwork = async () => {
    if (!walletClient) return;
    try {
      await walletClient.switchChain({
        id: getEnvChain().id,
      });
      checkTransactionStatus();
    } catch (error) {
      console.error('Failed to switch network:', error);
    }
  };

  const approveAllowance = async () => {
    if (!walletClient || !publicClient) return;
    try {
      setWaitingForTransaction(true);
      const stakeAmount = parseUnits(totalBet, usdc.decimals);
      const approveTxHash = await walletClient.writeContract({
        address: usdc.address,
        abi: erc20Abi,
        functionName: 'approve',
        args: [tokenRacer, stakeAmount],
      });
      await publicClient.waitForTransactionReceipt({
        hash: approveTxHash,
      });
      setWaitingForTransaction(false);
      checkTransactionStatus();
    } catch (error) {
      console.error('Failed to approve allowance:', error);
    } finally {
      setWaitingForTransaction(false);
    }
  };

  const createRace = async () => {
    if (!walletClient || !publicClient) return;
    try {
      setWaitingForTransaction(true);

      const { tokenIds, betAmounts } = Object.entries(bets).reduce(
        (acc, [tokenId, betAmount]) => {
          if (!isNaN(Number(betAmount)) && Number(betAmount) > 0) {
            acc.tokenIds.push(tokenId);
            acc.betAmounts.push(parseUnits(betAmount, usdc.decimals));
          }
          return acc;
        },
        { tokenIds: [] as string[], betAmounts: [] as bigint[] }
      );

      const createRaceTxHash = await walletClient.writeContract({
        address: tokenRacer,
        abi: tokenRacerAbi,
        functionName: 'placeBets',
        args: [BigInt(race.id), tokenIds, betAmounts, walletClient.account.address],
      });
      await publicClient.waitForTransactionReceipt({
        hash: createRaceTxHash,
      });
      setWaitingForTransaction(false);
      onClose();
    } catch (error) {
      console.error('Failed to create race:', error);
    } finally {
      setWaitingForTransaction(false);
    }
  };

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    checkTransactionStatus();
  }, [checkTransactionStatus, chainId]);

  if (!show) return null;

  return (
    <div className={`modal-overlay`}>
      <div className="bet-selector relative rounded-md" ref={modalRef}>
        <div className="absolute right-4 top-4 cursor-pointer" onClick={onClose}>
          <XIcon size={24} className="text-white/50" />
        </div>

        <div className="bet-selector-title">
          <h2 className="text-xl font-bold">Staking Confirmation</h2>
        </div>
        <div className="header !mb-6 text-lg">
          <h2>
            {race.name} #{race.id}
          </h2>
        </div>

        <div className="mb-4 flex flex-col gap-2">
          {race.tokenStats
            .filter(tokenStat => !isNaN(Number(bets[tokenStat.token.id])) && Number(bets[tokenStat.token.id]) > 0)
            .map(tokenStat => {
              const totalBet = Object.values(bets).reduce(
                (acc, bet) => acc + (isNaN(Number(bet)) ? 0 : Number(bet)),
                0
              );
              const betAmount = bets[tokenStat.token.id];
              const poolAmount = Number(
                formatUnits((race.totalPool * (BigInt(1e18) - race.creatorFeePercent)) / BigInt(1e18), usdc.decimals)
              );
              const tokenAmount = Number(formatUnits(tokenStat.totalBetsAmount, usdc.decimals));
              const ratio = calculateRewardMultiplier(poolAmount + totalBet, tokenAmount + Number(betAmount));

              // Calculate potential reward based on new ratio
              const [numerator, denominator] = ratio.split(':').map(Number);
              const rewardMultiplier = denominator > 0 ? numerator / denominator : 0;
              const potentialReward = Number(betAmount) * (rewardMultiplier - 1); // Subtract bet amount to get just the reward

              return (
                <div key={tokenStat.token.id} className="flex items-center gap-2 p-2">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full">
                    <img
                      src={tokenStat.token.marketData?.image}
                      alt={`${tokenStat.token.symbol}`}
                      className="object-contain"
                    />
                  </div>
                  <div className="flex w-full gap-2 pl-4">
                    <div className="flex flex-1 flex-col">
                      <div>{tokenStat.token.symbol}</div>
                      <div className="text-xs text-gray-400">Potential Staking Reward Multiplier:</div>
                      <div className="text-sm text-white">{ratio}</div>
                    </div>
                    <div className="flex flex-1 flex-col items-end">
                      <div className="flex gap-1">
                        <span className="text-gray-400">$</span> <span>{betAmount}</span>
                      </div>
                      <div className="text-xs text-gray-400">Race Outcome Based Potential Staking Reward:</div>
                      <div className="text-sm text-gray-400">
                        ${betAmount} + ${potentialReward.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        <button
          disabled={!checkIsActionable()}
          className="w-full rounded-md py-2 font-bold text-white"
          onClick={getTransactionAction()}
        >
          {getTransactionActionText()}
        </button>
        <button className="secondary mt-3 w-full rounded-md py-2 font-bold text-white" onClick={onClose}>
          Back
        </button>
      </div>
    </div>
  );
};

export default BetPreview;
